import { createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { ProjectChange } from "Types";
import {
  clearVersions,
  packageCreated,
  packageDeleted,
  projectCreated,
  projectDeleted,
  versionCreated,
  versionDeleted,
} from "Utils";

type ItemState = {
  created: ProjectChange[];
  deleted: ProjectChange[];
};

export type ProjectsSliceState = {
  projects: ItemState;
  packages: ItemState;
  versions: ItemState;
};

const initialState: ProjectsSliceState = {
  projects: {
    created: [],
    deleted: [],
  },
  packages: {
    created: [],
    deleted: [],
  },
  versions: {
    created: [],
    deleted: [],
  },
};

export const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    clearAllProjectData: () => {
      return cloneDeep(initialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(projectCreated, (state, action) => {
      state.projects.created.push(...action.payload);
    });
    builder.addCase(projectDeleted, (state, action) => {
      state.projects.created = state.projects.created.filter(
        (createdProject) =>
          !action.payload.some(
            (deletedProject) => createdProject.id === deletedProject.id,
          ),
      );
      state.projects.deleted.push(...action.payload);
    });
    builder.addCase(packageCreated, (state, action) => {
      state.packages.created.push(...action.payload);
    });
    builder.addCase(packageDeleted, (state, action) => {
      state.packages.created = state.packages.created.filter(
        (createdPackage) =>
          !action.payload.some(
            (deletedPackage) => deletedPackage.id === createdPackage.id,
          ),
      );
      state.packages.deleted.push(...action.payload);
    });
    builder.addCase(versionCreated, (state, action) => {
      state.versions.created.push(...action.payload);
    });
    builder.addCase(versionDeleted, (state, action) => {
      state.versions.created = state.versions.created.filter(
        (createdVersion) =>
          !action.payload.some(
            (deletedVersion) => deletedVersion.id === createdVersion.id,
          ),
      );
      state.versions.deleted.push(...action.payload);
    });
    builder.addCase(clearVersions, (state) => {
      state.versions = {
        created: [],
        deleted: [],
      };
    });
  },
  selectors: {
    selectDeletedProjects: (state): ProjectChange[] => state.projects.deleted,
    selectDeletedPackages: (state): ProjectChange[] => state.packages.deleted,
    selectCreatedVersions: (state): ProjectChange[] => state.versions.created,
    selectDeletedVersions: (state): ProjectChange[] => state.versions.deleted,
  },
});

export const { clearAllProjectData } = projectsSlice.actions;

export const {
  selectDeletedProjects,
  selectDeletedPackages,
  selectCreatedVersions,
  selectDeletedVersions,
} = projectsSlice.selectors;

export default projectsSlice.reducer;
